import { c as _c } from "react/compiler-runtime";
import { cva, cx } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import Shape from './Shape';
export enum MaskType {
  Mask1 = 'Mask1',
  Mask2 = 'Mask2',
  Mask3 = 'Mask3',
  Mask4 = 'Mask4',
  Mask5 = 'Mask5',
}
export type MaskShapesColorVariant = 'yellow-orange' | 'yellow-salmon' | 'yellow-turqoise';
type ShapeColor = NonNullable<ComponentProps<typeof Shape>['color']>;
export type MaskShapesProps = {
  maskType: MaskType;
  /** @default 'right' */
  assetPosition?: 'left' | 'right';
  /** @default false */
  hasBackgroundImage?: boolean;
  /** @default MaskShapesColorVariant.YellowOrange */
  colorVariant?: MaskShapesColorVariant;
  className?: string;
};
export function MaskShapes(t0) {
  const $ = _c(32);
  const {
    maskType,
    assetPosition: t1,
    colorVariant: t2,
    hasBackgroundImage: t3,
    className
  } = t0;
  const assetPosition = t1 === undefined ? "right" : t1;
  const colorVariant = t2 === undefined ? "yellow-orange" : t2;
  const hasBackgroundImage = t3 === undefined ? false : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      "yellow-orange": ["fill-yellow-500", "fill-tertiary"],
      "yellow-salmon": ["fill-yellow-500", "fill-salmon-500"],
      "yellow-turqoise": ["fill-yellow-500", "fill-secondary"]
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const colorMapping = t4;
  const color = colorMapping[colorVariant] ?? colorMapping["yellow-turqoise"];
  switch (maskType) {
    case "Mask1":
      {
        const t5 = color[0];
        const t6 = assetPosition === "right" ? "-ml-10" : "right-0 z-1";
        let t7;
        if ($[1] !== className || $[2] !== t6) {
          t7 = cx("rotate-30 absolute top-[60px] scale-75", t6, className);
          $[1] = className;
          $[2] = t6;
          $[3] = t7;
        } else {
          t7 = $[3];
        }
        let t8;
        if ($[4] !== color[0] || $[5] !== t7) {
          t8 = <Shape type="pacMan" color={t5} className={t7} />;
          $[4] = color[0];
          $[5] = t7;
          $[6] = t8;
        } else {
          t8 = $[6];
        }
        const t9 = color[1];
        const t10 = assetPosition === "right" ? "-rotate-60 -bottom-[45px] right-[5%] z-1" : "-rotate-98 bottom-[10px] left-[4%] md:-bottom-[85px] md:-left-[5%]";
        let t11;
        if ($[7] !== className || $[8] !== t10) {
          t11 = cx("absolute lg:scale-125", t10, className);
          $[7] = className;
          $[8] = t10;
          $[9] = t11;
        } else {
          t11 = $[9];
        }
        let t12;
        if ($[10] !== color[1] || $[11] !== t11) {
          t12 = <Shape type="halfCircle" color={t9} className={t11} />;
          $[10] = color[1];
          $[11] = t11;
          $[12] = t12;
        } else {
          t12 = $[12];
        }
        let t13;
        if ($[13] !== t12 || $[14] !== t8) {
          t13 = <>{t8}{t12}</>;
          $[13] = t12;
          $[14] = t8;
          $[15] = t13;
        } else {
          t13 = $[15];
        }
        return t13;
      }
    case "Mask2":
      {
        const t5 = color[0];
        const t6 = hasBackgroundImage ? "ml-10" : "-ml-16";
        const t7 = assetPosition !== "right" && "hidden";
        let t8;
        if ($[16] !== className || $[17] !== t6 || $[18] !== t7) {
          t8 = cx("scale- -rotate-20 absolute top-[90px] z-1 hidden scale-75 lg:block", t6, t7, className);
          $[16] = className;
          $[17] = t6;
          $[18] = t7;
          $[19] = t8;
        } else {
          t8 = $[19];
        }
        let t9;
        if ($[20] !== color[0] || $[21] !== t8) {
          t9 = <Shape type="pacMan" color={t5} className={t8} />;
          $[20] = color[0];
          $[21] = t8;
          $[22] = t9;
        } else {
          t9 = $[22];
        }
        const t10 = color[1];
        const t11 = assetPosition !== "right" && "hidden";
        let t12;
        if ($[23] !== className || $[24] !== t11) {
          t12 = cx("-rotate-150 lg:rotate-30 absolute -left-[100px] z-1 mt-[150px] scale-150 lg:-bottom-[100px] lg:left-auto lg:right-[25px] lg:mt-0", t11, className);
          $[23] = className;
          $[24] = t11;
          $[25] = t12;
        } else {
          t12 = $[25];
        }
        let t13;
        if ($[26] !== color[1] || $[27] !== t12) {
          t13 = <Shape type="halfCircle" color={t10} className={t12} />;
          $[26] = color[1];
          $[27] = t12;
          $[28] = t13;
        } else {
          t13 = $[28];
        }
        let t14;
        if ($[29] !== t13 || $[30] !== t9) {
          t14 = <>{t9}{t13}</>;
          $[29] = t13;
          $[30] = t9;
          $[31] = t14;
        } else {
          t14 = $[31];
        }
        return t14;
      }
    default:
      {
        return null;
      }
  }
}
export const MaskVariant = cva('', {
  variants: {
    mask: {
      [MaskType.Mask1]: 'mask1 md:mt-O min-h-[330px]',
      [MaskType.Mask2]: 'mask2 min-h-[360px]',
      [MaskType.Mask3]: 'mask3 min-h-[360px]',
      [MaskType.Mask4]: 'mask4 min-h-[360px]',
      [MaskType.Mask5]: 'mask5 min-h-[360px]'
    }
  }
});
export default MaskVariant;